import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Store, Trash2 } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface MarketplaceChannel {
  id: string;
  name: string;
  platform: string;
  status: string;
  pricing_rule_type: string;
  pricing_rule_value: number;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  has_credentials: boolean;
  listings_count: number;
  active_listings_count: number;
  created_at: string;
}

interface ChannelsResponse {
  channels: MarketplaceChannel[];
  pagination: PaginationData;
}

const PLATFORM_LABELS: Record<string, string> = {
  google_shopping: 'Google Shopping',
  facebook_shopping: 'Facebook/Instagram',
  amazon: 'Amazon',
  ebay: 'eBay',
  etsy: 'Etsy',
  walmart: 'Walmart',
  tiktok_shop: 'TikTok Shop',
};

const PLATFORM_COLORS: Record<string, any> = {
  google_shopping: 'blue',
  facebook_shopping: 'indigo',
  amazon: 'yellow',
  ebay: 'red',
  etsy: 'orange',
  walmart: 'blue',
  tiktok_shop: 'pink',
};

const STATUS_COLORS: Record<string, any> = {
  active: 'green',
  inactive: 'gray',
  paused: 'yellow',
  error: 'red',
};

const SYNC_COLORS: Record<string, any> = {
  success: 'green',
  partial: 'yellow',
  error: 'red',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const columns: ColumnDef<MarketplaceChannel>[] = [
  {
    id: 'name',
    header: 'Channel',
    accessorKey: 'name',
    sortable: true,
    cell: ({ row }) => (
      <Link to={`/admin/marketplace/channels/${row.id}`} className="block group">
        <div className="font-medium text-gray-900 group-hover:text-blue-600">{row.name}</div>
      </Link>
    ),
  },
  {
    id: 'platform',
    header: 'Platform',
    accessorKey: 'platform',
    cell: ({ row }) => (
      <Badge variant={PLATFORM_COLORS[row.platform] || 'gray'}>
        {PLATFORM_LABELS[row.platform] || row.platform}
      </Badge>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => (
      <Badge variant={STATUS_COLORS[row.status] || 'gray'}>{row.status}</Badge>
    ),
  },
  {
    id: 'listings',
    header: 'Listings',
    align: 'right',
    cell: ({ row }) => (
      <span className="text-sm text-gray-600">
        {row.active_listings_count} active / {row.listings_count} total
      </span>
    ),
  },
  {
    id: 'last_sync',
    header: 'Last Sync',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">{timeAgo(row.last_sync_at)}</span>
        {row.last_sync_status && (
          <Badge variant={SYNC_COLORS[row.last_sync_status] || 'gray'}>
            {row.last_sync_status}
          </Badge>
        )}
      </div>
    ),
  },
];

const MarketplaceChannels = () => {
  const [channels, setChannels] = useState<MarketplaceChannel[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState } = useDataTableState({
    storageKey: 'admin-marketplace-channels',
    defaultSort: 'name',
    defaultLimit: 25,
    columns,
  });

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        const response = await api.get<ChannelsResponse>(`/admin/marketplace-channels?${params.toString()}`);
        setChannels(response.channels);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load channels');
      } finally {
        setIsLoading(false);
      }
    };
    fetchChannels();
  }, [state.page, state.limit, state.search, state.sort, state.order]);

  const bulkActions: BulkAction<MarketplaceChannel>[] = [
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: <Trash2 className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Are you sure you want to deactivate the selected channels?',
      onExecute: async (rows) => {
        for (const row of rows) {
          await api.delete(`/admin/marketplace-channels/${row.id}`);
        }
        setChannels((prev) => prev.map((ch) =>
          rows.some((r) => r.id === ch.id) ? { ...ch, status: 'inactive' } : ch
        ));
        addToast(`${rows.length} channel(s) deactivated`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketplace Channels</h1>
          <p className="text-gray-600 mt-1">Manage sales channels and shopping feeds</p>
        </div>
        <Link to="/admin/marketplace/channels/new">
          <Button variant="primary" size="md">
            <Plus className="w-5 h-5 mr-2" />
            Add Channel
          </Button>
        </Link>
      </div>

      <DataTable
        columns={columns}
        data={channels}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        bulkActions={bulkActions}
        searchPlaceholder="Search channels by name..."
        emptyState={
          <div className="p-12 text-center">
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No channels yet</h3>
            <p className="text-gray-600 mb-6">Add your first sales channel to start listing products</p>
            <Link to="/admin/marketplace/channels/new">
              <Button variant="primary" size="md">
                <Plus className="w-5 h-5 mr-2" />
                Add Channel
              </Button>
            </Link>
          </div>
        }
      />
    </div>
  );
};

export default MarketplaceChannels;
